/**
 * Sidebar/MenuItem/index.tsx
 * desc: one row of the v2 sidebar — an icon + label link to a navigation item's route
 * note: active when the location sits under the item's path (so detail pages light up their list item) or under
 *       any of its `activeOn` routes; collapsed rows drop the label and expose it as a tooltip + accessible name.
 *       The `child` variant is the text-only row inside an accordion (no icon, no active bar).
 */

import { Link, useLocation } from 'react-router';
import Tooltip from '../../Tooltip/typed';
import { classNames } from '../../../utilities';
import { trackEvent } from '../../../utilities/analytics';
import { translate } from '../../../utilities/localization';
import type { NavItem } from '../config';
import { COLLAPSED_TOOLTIP_OFFSET } from '../constants';
import { getNavItemPath, isNavItemActive } from '../utilities';

interface MenuItemProps {
    item: NavItem;
    isCollapsed: boolean;
    variant?: 'row' | 'child';
}

const MenuItem = ({ item, isCollapsed, variant = 'row' }: MenuItemProps) => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    const { pathname } = useLocation();
    const label = translate(item.labelKey);
    const Icon = item.icon;
    // Variables
    const isActive = isNavItemActive(item, pathname);


    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handleClick = () => trackEvent(`Global: Sidebar - ${item.id} Link (Click)`);


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <Tooltip arrow={ false } asChild side="right" sideOffset={ COLLAPSED_TOOLTIP_OFFSET } text={ isCollapsed ? label : null }>
            <Link
                to={ getNavItemPath(item) }
                className={ classNames('row', { child: variant === 'child', selected: isActive }) }
                aria-current={ isActive ? 'page' : undefined }
                aria-label={ isCollapsed ? label : undefined }
                onClick={ handleClick }
            >
                { Icon ? <Icon readOnly tooltip={ null }/> : null }
                { !isCollapsed ? <span className="label">{ label }</span> : null }
            </Link>
        </Tooltip>
    );
};

export default MenuItem;
